// @ts-expect-error
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';
import { MAX_DROP_TO_SURVEY_IMAGE_DISTANCE_METERS } from './surveyImageDrag';
import { isMapPointOutsidePaddedExtent } from './surveyImageDragHitTest';

export type HighlightedSurveyDropCache = {
  readonly slug: string;
  readonly geometries: readonly unknown[];
};

export type MapPointLike = {
  readonly x?: number;
  readonly y?: number;
  readonly spatialReference?: { readonly wkid?: number };
};

/**
 * Returns true when the drop point is within maxDistance of any cached
 * highlighted-survey geometry for the same survey slug.
 */
export function isMapPointOnHighlightedSurvey(
  mapPoint: MapPointLike,
  surveySlug: string,
  cached: HighlightedSurveyDropCache | null,
  measureDistanceMeters: (
    from: MapPointLike,
    geometry: unknown,
  ) => number | null,
  maxDistanceMeters: number = MAX_DROP_TO_SURVEY_IMAGE_DISTANCE_METERS,
): boolean {
  if (!cached || cached.slug !== surveySlug || cached.geometries.length === 0) {
    return false;
  }

  for (const geometry of cached.geometries) {
    const extent = (geometry as { extent?: {
      xmin?: number;
      ymin?: number;
      xmax?: number;
      ymax?: number;
    } | null })?.extent;
    const looksGeographic =
      extent != null &&
      typeof extent.xmin === 'number' &&
      typeof extent.xmax === 'number' &&
      extent.xmax - extent.xmin <= 360;
    const extentPad = looksGeographic
      ? maxDistanceMeters / 111_320
      : maxDistanceMeters;
    if (isMapPointOutsidePaddedExtent(mapPoint, geometry, extentPad)) {
      continue;
    }
    const measured = measureDistanceMeters(mapPoint, geometry);
    if (
      typeof measured === 'number' &&
      Number.isFinite(measured) &&
      measured <= maxDistanceMeters
    ) {
      return true;
    }
  }

  return false;
}

/**
 * Snaps a map drop to the nearest point on highlighted survey
 * (same geometry used for drop validation).
 */
export function resolveNearestPointOnGeometries(
  mapPoint: MapPointLike,
  geometries: readonly unknown[],
): MapPointLike | null {
  if (geometries.length === 0) {
    return null;
  }
  if (typeof mapPoint.x !== 'number' || typeof mapPoint.y !== 'number') {
    return null;
  }

  let bestPoint: MapPointLike | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const geometry of geometries) {
    try {
      const nearest = geometryEngine.nearestCoordinate(
        geometry,
        mapPoint,
      ) as { coordinate?: MapPointLike } | null;
      const coordinate = nearest?.coordinate;
      if (
        !coordinate ||
        typeof coordinate.x !== 'number' ||
        typeof coordinate.y !== 'number'
      ) {
        continue;
      }
      const measured = geometryEngine.distance(
        mapPoint,
        coordinate,
        'meters',
      ) as number;
      if (
        typeof measured === 'number' &&
        Number.isFinite(measured) &&
        measured < bestDistance
      ) {
        bestDistance = measured;
        bestPoint = coordinate;
      }
    } catch {
      // Try the next geometry segment.
    }
  }

  return bestPoint;
}

export function isSurveyImageDragMimePresent(
  types: ReadonlyArray<string> | DOMStringList | null | undefined,
  mime: string,
): boolean {
  if (!types) {
    return false;
  }
  // Avoid Array.from allocation on every dragover (Firefox/Safari fire very often).
  const list = types as {
    length: number;
    item?: (i: number) => string | null;
    [i: number]: string;
  };
  const len = list.length;
  for (let i = 0; i < len; i += 1) {
    const value = typeof list.item === 'function' ? list.item(i) : list[i];
    if (value === mime) {
      return true;
    }
  }
  return false;
}
